/**
 * Shared building blocks for the Capabilities and Connections pages. Both derive
 * their view from the same catalog (`category` + `system`) via `buildModel`; the
 * copy maps and the shared Test-result banner live here so the two pages stay
 * thin and consistent.
 */
import { Banner, SCLogo } from '@servicecompressionllc/sc-design-system';
import type { StatusTone } from '@servicecompressionllc/sc-design-system';
import type {
  IntegrationDefinition,
  TenantIntegrationSummary,
  TenantIntegrationTestErrorCode,
  TenantIntegrationTestResult,
} from '../../lib/types.js';

// ─── copy / labels ──────────────────────────────────────────────────────

const SYSTEM_DISPLAY: Record<string, string> = {
  microsoft: 'Microsoft Teams',
  netsuite: 'Oracle NetSuite',
  mlink: 'Murphy M-Link',
  enbase: 'Detechtion Enbase',
  azure_ai_search: 'Azure AI Search',
  sc_platform: 'Service Compression',
  samsara: 'Samsara',
  custom: 'Custom API',
  mcp: 'Custom MCP',
};
export const systemDisplay = (s: string) => SYSTEM_DISPLAY[s] ?? s;

/**
 * The tile beside a connection's name. Every vendor gets its monogram; ours
 * gets the actual mark — an SC-hosted source is the one card on this page that
 * isn't a third party, and "S" in a grey box reads like a vendor we forgot to
 * brand.
 */
export const SystemAvatar = ({ system, title }: { system: string; title: string }) => (
  <div className="bg-surface text-ink-muted border-outline flex h-11 w-11 flex-none items-center justify-center rounded-[10px] border text-base font-bold">
    {system === 'sc_platform' ? (
      <SCLogo className="h-5 w-auto" aria-label="Service Compression" />
    ) : (
      title.charAt(0)
    )}
  </div>
);

const CAPABILITY_LABELS: Record<string, string> = {
  notifications: 'Notifications',
  telemetry: 'Telemetry',
  work_history: 'Work History',
  schedule: 'Schedule',
  directory: 'Directory',
  regions: 'Regions',
  assets: 'Assets',
  knowledge_base: 'Knowledge Base',
  fleet_location: 'Fleet Location',
  inventory: 'Inventory',
};
export const CAPABILITY_DESC: Record<string, string> = {
  notifications:
    'Personal notifications and approval requests, delivered using organization defaults and user preferences.',
  telemetry: 'Live sensor readings from compressor packages.',
  work_history: 'Work orders, service records, and parts used.',
  schedule: "Each mechanic's scheduled jobs — date, site, unit, and job type.",
  directory:
    'Resolve a mechanic by name to their record (email, region) — lets the agent look someone up by name.',
  regions:
    'The dispatch territory catalog — the region vocabulary units and mechanics are tagged with, so the agent can ground a region and dispatch by it.',
  assets: 'Equipment and unit master data.',
  knowledge_base:
    'Troubleshooting knowledge the agent searches, plus the source manuals it fetches by component.',
  fleet_location: 'Live truck GPS positions — where each vehicle is, for dispatch.',
  inventory: 'Parts inventory and stock levels.',
};
export const capabilityLabel = (c: string) =>
  CAPABILITY_LABELS[c] ?? (c ? c.charAt(0).toUpperCase() + c.slice(1).replace(/_/g, ' ') : 'Data');

const FRIENDLY_TEST_ERROR: Record<TenantIntegrationTestErrorCode, string> = {
  invalid_credentials:
    'The vendor rejected the credentials. They may have been rotated. Update them in Edit and test again.',
  vendor_unreachable:
    "The vendor's API didn't respond. This is usually transient — wait a minute and retry. If it persists, the vendor may be having an outage.",
  rate_limited: 'The vendor is rate-limiting requests right now. Wait a few minutes and retry.',
  unknown:
    'Something went wrong testing the connection. See the technical detail below or contact support.',
};

// ─── derivation (capability ← sources; the shared connection per system) ──

interface Capability {
  category: string;
  sources: IntegrationDefinition[];
}
export interface ConnectionDef {
  key: string;
  system: string;
  /** The capability it serves — the identity of a source that has no vendor. */
  category: string;
  /** False when the source asks the tenant for nothing (an SC-hosted one). */
  hasFields: boolean;
}

/**
 * What to call a card.
 *
 * A vendor connection is named by its vendor: you are connecting an Oracle
 * NetSuite account, and which capabilities it feeds comes later. An SC-hosted
 * source has no vendor and no credentials, so naming it "Service Compression"
 * answers a question nobody asked — what the operator wants to see is the
 * thing it provides. Name it by the capability instead.
 */
/**
 * The honest state of a configured row. `lastTestedOk` is null until something
 * tests it, and reporting that as Connected claimed a working connection nobody
 * had checked. Shared by the Connections cards and the Custom MCP detail page.
 */
export const connectionStatus = (
  row: TenantIntegrationSummary | undefined,
): { tone: StatusTone; label: string } =>
  !row
    ? { tone: 'neutral', label: 'Not connected' }
    : row.isConnected === false
      ? { tone: 'neutral', label: 'Disconnected' }
      : row.lastTestedOk === true
        ? { tone: 'success', label: 'Connected' }
        : row.lastTestedOk === false
          ? { tone: 'danger', label: 'Connection failed' }
          : { tone: 'neutral', label: 'Not tested yet' };

export const connectionTitle = (def: ConnectionDef, name?: string): string => {
  if (name) return name;
  // A `connection` entry is a credential bundle — always named by its vendor,
  // whatever its field list looks like. Only a SOURCE that asks for nothing
  // has no vendor to name it by.
  const vendorless =
    def.system === 'sc_platform' && def.category !== 'connection' && !def.hasFields;
  return vendorless ? capabilityLabel(def.category) : systemDisplay(def.system);
};

export const buildModel = (defs: IntegrationDefinition[]) => {
  const isConnection = (e: IntegrationDefinition) => e.category === 'connection';
  const connKeyForSystem = (system: string): string | undefined =>
    defs.find((e) => isConnection(e) && e.system === system)?.key;
  const sourceConnKey = (e: IntegrationDefinition): string => connKeyForSystem(e.system) ?? e.key;

  // Sorted alphabetically for readability — these lists grow as more customers
  // and providers are onboarded. Capability sections by their display label;
  // the source rows within each, and the connection accounts, by system name.
  const bySystem = (a: { system: string }, b: { system: string }) =>
    systemDisplay(a.system).localeCompare(systemDisplay(b.system));

  // Categories are derived from the catalog itself — every non-connection
  // category the API returns gets a card. A new server-side integration category
  // renders with no admin change; CAPABILITY_LABELS / CAPABILITY_DESC are
  // optional presentational overrides (both fall back gracefully — see
  // capabilityLabel and the page description fallback), NOT an allowlist. Sorted
  // by display label for a stable, readable order.
  const categories = [...new Set(defs.filter((e) => !isConnection(e)).map((e) => e.category))];
  const capabilities: Capability[] = categories
    .map((category) => ({
      category,
      sources: defs.filter((e) => !isConnection(e) && e.category === category).sort(bySystem),
    }))
    .filter((c) => c.sources.length > 0)
    .sort((a, b) => capabilityLabel(a.category).localeCompare(capabilityLabel(b.category)));

  const connections: ConnectionDef[] = [];
  const seen = new Set<string>();
  const toConnection = (e: IntegrationDefinition, key: string): ConnectionDef => ({
    key,
    system: e.system,
    category: e.category,
    hasFields: e.fields.length > 0,
  });
  for (const e of defs) {
    if (isConnection(e) && !seen.has(e.key)) {
      connections.push(toConnection(e, e.key));
      seen.add(e.key);
    }
  }
  for (const e of defs) {
    if (isConnection(e)) continue;
    // A per-capability custom source (custom_api_*) is backed by the shared
    // `custom_api` connection, which is already listed above via its connection
    // catalog entry — so the domain source itself never appears as an account.
    // Real self-contained vendors (mlink, samsara, …) still appear as theirs.
    if (e.system === 'custom') continue;
    const ck = sourceConnKey(e);
    if (!seen.has(ck)) {
      connections.push(toConnection(e, ck));
      seen.add(ck);
    }
  }
  connections.sort(bySystem);

  return { capabilities, connections, sourceConnKey };
};

// ─── presentational ───────────────────────────────────────────────────────

/**
 * The result banner shown after a Test action — shared by both pages.
 *
 * Takes a resolved `label` rather than a key: a key no longer names one thing, so
 * the caller (which knows which instance it tested) supplies the name to show.
 */
export const TestResultBanner = ({
  testResult,
}: {
  testResult: { label: string; result: TenantIntegrationTestResult } | null;
}) => {
  if (!testResult) return null;
  return (
    <Banner variant={testResult.result.ok ? 'success' : 'danger'} className="mb-4">
      <Banner.Body>
        <Banner.Title>
          {`${testResult.label} ` +
            (testResult.result.ok ? 'connection succeeded.' : 'connection failed.')}
        </Banner.Title>
        {!testResult.result.ok && (
          <>
            <Banner.Description>
              {FRIENDLY_TEST_ERROR[testResult.result.code ?? 'unknown']}
            </Banner.Description>
            {testResult.result.error && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer opacity-75 hover:opacity-100">
                  Technical detail
                </summary>
                <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] opacity-75">
                  {testResult.result.error}
                </pre>
              </details>
            )}
          </>
        )}
      </Banner.Body>
    </Banner>
  );
};

/**
 * The everyday name of a login system from its OAuth issuer, for "signs in with
 * Microsoft". Falls back to the host so an unknown system still reads as something.
 */
export const loginProviderName = (issuer: string | null): string => {
  if (!issuer) return 'its own login';
  let host: string;
  try {
    host = new URL(issuer).hostname;
  } catch {
    return issuer;
  }
  if (
    host === 'login.microsoftonline.com' ||
    host === 'sts.windows.net' ||
    host.endsWith('.ciamlogin.com') ||
    host.endsWith('.b2clogin.com')
  ) {
    return 'Microsoft';
  }
  if (host === 'accounts.google.com') return 'Google';
  if (host.endsWith('atlassian.com')) return 'Atlassian';
  if (host.endsWith('.okta.com') || host.endsWith('.oktapreview.com')) return 'Okta';
  if (host === 'github.com') return 'GitHub';
  return host;
};
